import gql from 'graphql-tag';

/**
 * AXON-3585: everything the clinic-wide DT capacity view needs, in one request.
 *
 * Deliberately light — no per-clinician event windows. The six-week look-ahead
 * that finds not-yet-started series belongs to the day grid, not here.
 */
export const GET_CLINIC_DT_CAPACITY = gql`
  query getClinicDtCapacity {
    clinics {
      id
      name
      isActive
      clinicHub {
        id
      }
    }
    clinicDtCapacity {
      clinicId
      discipline
      openSlots
      clinicians
      therapistOpenSlots
      assistantOpenSlots
      cliniciansWithoutCareSessions
    }
  }
`;

/**
 * AXON-3585: one clinic's DT clinicians, for the single-clinic view.
 *
 * Clinicians, not roll-ups — the grade and discipline rows are summed from
 * these on the client, which is what keeps the weekday figures adding up to
 * Open at every depth. Still no event windows.
 *
 * The clinic's name comes from the same `clinics` list the estate-wide view
 * uses, rather than the single-clinic `clinic` query, which declares its id as
 * a String and would need the one value passed in two forms. Drilling in
 * usually finds the list already in the cache, and a direct URL load costs one
 * short query for a few dozen names.
 */
export const GET_CLINIC_DT_CLINICIAN_CAPACITY = gql`
  query getClinicDtClinicianCapacity($clinicId: Int!) {
    clinics {
      id
      name
    }
    clinicDtClinicianCapacity(clinicId: $clinicId) {
      providerId
      firstName
      lastName
      displayName
      specialtyAbbreviation
      discipline
      isAssistantGrade
      supervisorName
      hasWeeklyLimit
      templateSessions
      bookedSessions
      openSessions
    }
  }
`;

/**
 * AXON-3585: one clinic's clinicians as columns on a single weekday.
 *
 * This is the one level that reads a multi-week event window — the six-week
 * look-ahead that finds series which have not started yet. It is affordable
 * precisely because it is one clinic and one weekday at a time.
 */
export const GET_CLINIC_DAY_GRID = gql`
  query getClinicDayGrid($clinicId: Int!, $weekday: Int!) {
    clinics {
      id
      name
      # The week view resolves Starts and Ending dates in the clinic's zone. A
      # grid reading them in the browser's would print a different date at the
      # boundaries, which is exactly the inconsistency this view must not have.
      timezone
    }
    clinicDayGrid(clinicId: $clinicId, weekday: $weekday) {
      providerId
      firstName
      lastName
      displayName
      specialtyAbbreviation
      discipline
      isAssistantGrade
      dayBooked
      dayTarget
      weekBooked
      weekTarget
      hasNoTemplate
      hasNoBookedData
      availabilities
      events
    }
  }
`;
